import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { LoadingContext } from '../LoadingContext';
import ProductCard from './ProductCard';
import PromotionProductCard from './PromotionProductCard';
import CategoryCard from './CategoryCard';
import CategoryList from './CategoryList';
import './SearchResults.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function SearchResults() {
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);
  const { setIsLoading } = useContext(LoadingContext);
  const [loading, setLoading] = useState(true);
  const [isExpandedView] = useState(true);
  const [promotions, setPromotions] = useState([]);

  useEffect(() => {
    const controller = new AbortController();
    const query = searchQuery.trim();

    const fetchJson = async (url, fallback) => {
      try {
        const response = await fetch(url, { signal: controller.signal });
        return response.ok ? await response.json() : fallback;
      } catch {
        return fallback;
      }
    };

    const fetchSearchResults = async () => {
      if (!query) {
        setProducts([]);
        setCategories([]);
        setCategoryTree([]);
        setPromotions([]);
        setLoading(false);
        setIsLoading(false);
        return;
      }

      setLoading(true);
      setIsLoading(true);
      const term = encodeURIComponent(query);
      const [productsData, categoriesData, categoryTreeData, promotionsData] = await Promise.all([
        fetchJson(`${API_BASE_URL}/Products?SearchString=${term}`, { products: [] }),
        fetchJson(`${API_BASE_URL}/api/categories/search?term=${term}`, []),
        fetchJson(`${API_BASE_URL}/api/categories/tree`, []),
        fetchJson(`${API_BASE_URL}/api/Promotions`, []),
      ]);

      // An earlier search must not overwrite the current query's results.
      if (controller.signal.aborted) return;

      setProducts(Array.isArray(productsData?.products) ? productsData.products : []);
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      setCategoryTree(Array.isArray(categoryTreeData) ? categoryTreeData : []);
      setPromotions(Array.isArray(promotionsData) ? promotionsData : []);
      setLoading(false);
      setIsLoading(false);
    };

    fetchSearchResults();

    return () => {
      controller.abort();
      setIsLoading(false);
    };
  }, [searchQuery, setIsLoading]);

  const term = searchQuery.trim().toLowerCase();
  // This public endpoint already excludes inactive promotions and hidden products.
  const promotionProducts = promotions.filter(promotion => term && [
    promotion.productName,
    promotion.productCode,
    promotion.description,
    promotion.variantLabel,
  ].some(value => value?.toLowerCase().includes(term)));
  const promotedProductIds = new Set(
    promotionProducts.filter(promotion => !promotion.isStandalone && promotion.productId != null)
      .map(promotion => String(promotion.productId))
  );
  // Show a matching linked offer once, with its promotion details and price.
  const catalogueProducts = products.filter(product => !promotedProductIds.has(String(product.productID)));
  const promoMap = {};
  promotions.forEach(promotion => {
    if (!promotion.productId && !promotion.variantId) return;
    promoMap[promotion.variantId ? `v${promotion.variantId}` : `p${promotion.productId}`] = promotion.promotionPrice;
  });
  const noResults = !loading && catalogueProducts.length === 0 && promotionProducts.length === 0 && categories.length === 0;

  return (
    <div className="search-results-container">
      <div className="search-results-breadcrumb">
        <Link to="/">Home</Link>{" "}
        &gt; <span>Search Results for "{searchQuery}"</span>
      </div>

      <div className="search-results-main-container">
        {/* Category List Sidebar */}
        <aside className={`search-results-sidebar ${!isExpandedView ? 'collapsed' : ''}`}>
          <div className="search-results-sidebar-top">
            <h3 className="search-results-sidebar-heading">Categories</h3>
          </div>

          {/* Category Search Bar
          {isExpandedView && (
            <div className="category-search-container">
              <input
                type="text"
                className="category-search-input"
                placeholder="Search categories..."
                value={categorySearchTerm}
                onChange={(e) => searchSidebarCategories(e.target.value)}
              />
            </div>
          )} */}

          {isExpandedView && (
            <CategoryList key="search-category-list" categories={categoryTree} />
          )}
        </aside>

        {/* Main Content */}
        <main className={`search-results-content ${!isExpandedView ? 'expanded' : ''}`}>
          {noResults ? (
            <div className="no-results-message">
              <p>No products or categories found matching your search.</p>
              <p>Try a different search term or <Link to="/category">browse all products</Link>.</p>
            </div>
          ) : (
            <>
              {categories.length > 0 && (
                <div className="search-results-section">
                  <h2>Categories</h2>
                  <div className="search-results-grid">
                    {categories.map(category => (
                      <div className="search-results-card" key={category.categoryID}>
                        <CategoryCard category={category} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {promotionProducts.length > 0 && (
                <div className="search-results-section">
                  <h2>Promotion Products</h2>
                  <div className="search-results-grid">
                    {promotionProducts.map(promotion => (
                      <div className="search-results-card" key={`promotion-${promotion.id}`}>
                        <PromotionProductCard promotion={promotion} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {catalogueProducts.length > 0 && (
                <div className="search-results-section">
                  <h2>Products</h2>
                  <div className="search-results-grid">
                    {catalogueProducts.map(product => (
                      <div className="search-results-card" key={product.productID}>
                        <ProductCard product={product} promoPrice={promoMap[`p${product.productID}`]} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default SearchResults;
